import { useCreateCanvas } from "@/api/canvas";
import { useUpdateGlobalOnboardingStatus } from "@/api/onboarding";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import { FinishOnboardingCard as FinishOnboardingCardUI } from "@cohesive/ui-components";
import { Sparkles, X } from "lucide-react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface FinishOnboardingCardProps {
  /**
   * Hide the card locally. `setComplete` is fire-and-forget and only
   * updates the cached onboarding status once the round-trip lands, so
   * the parent bridges that gap with a local flag.
   */
  onDismiss: () => void;
  /**
   * `"card"` (default) is the grid tile. `"row"` renders a matching list row
   * for the home browser's list view.
   */
  variant?: "card" | "row";
}

export function FinishOnboardingCard({
  onDismiss,
  variant = "card",
}: FinishOnboardingCardProps) {
  const navigate = useNavigate();
  const { setInProgress, setComplete } = useUpdateGlobalOnboardingStatus();

  const createCanvas = useCreateCanvas({
    onSuccess: (canvas) => {
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }), {
        state: { seedOnboardingWidget: true },
      });
    },
    onError: () => {
      toast.error("Couldn't open a canvas. Please try again.");
    },
  });

  const handleStart = useCallback(() => {
    if (createCanvas.isPending) return;
    setInProgress();
    createCanvas.mutate({});
  }, [createCanvas, setInProgress]);

  const handleDismiss = useCallback(() => {
    if (createCanvas.isPending) return;
    setComplete();
    onDismiss();
  }, [createCanvas.isPending, setComplete, onDismiss]);

  if (variant === "row") {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label="Finish onboarding"
        onClick={handleStart}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleStart();
          }
        }}
        className={cn(
          "group flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-md px-2.5 py-1 text-left outline-none transition-colors",
          "hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
          createCanvas.isPending && "pointer-events-none opacity-70",
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#B86E49]/25 bg-gradient-to-br from-[#F5F0E5] to-[#EBE3D1] dark:from-[#221f19] dark:to-[#19160f]">
          <Sparkles className="size-4 text-[#B86E49]" />
        </span>

        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">
            Finish onboarding
          </span>
        </span>

        {/* Empty updated/owner columns keep the dismiss aligned with the
         *  rows' kebab lane. */}
        <span aria-hidden className="hidden w-24 shrink-0 @sm:block" />
        <span aria-hidden className="hidden w-32 shrink-0 @md:block" />

        <span
          className="flex w-7 shrink-0 justify-end"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Dismiss finish onboarding"
            onClick={handleDismiss}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-n-10 hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <FinishOnboardingCardUI
      onStart={handleStart}
      onDismiss={handleDismiss}
      className={cn(createCanvas.isPending && "pointer-events-none opacity-70")}
    />
  );
}
